import React, { useEffect, useState } from "react";
import styles from "./Contract.module.sass";
import cn from "classnames";
import Card from "../../../../components/Card/index.js";
import Loader from "../../../../components/Loader/index.js";
import Actions from "../../../../components/Actions/index.js";
import { isMinor } from "../../../../services/date.service.js";
import { signCerfa } from "../../../../services/signature.service.js";
import { downloadContract } from "../../../../services/contract.service.js";
import { toast } from "react-toastify";

const Contract = ({ className, contract, onContractUpdate, signatureStatus, folderId }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingInProgress, setSigningInProgress] = useState(false);

  /**
   * Gère le téléchargement du document CERFA
   */
  const handleDownloadCerfa = async () => {
    if (!contract || !contract.document) {
      toast.error("Aucun document CERFA disponible pour ce contrat");
      return;
    }

    try {
      const success = await downloadContract(contract);
      if (success) {
        toast.success("Document CERFA téléchargé avec succès!");
      } else {
        toast.error("Erreur lors du téléchargement du document CERFA");
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors du téléchargement du document CERFA");
    }
  };

  /**
   * Gère la demande de signature du document CERFA
   */
  const handleSignatureRequest = async () => {
    if (!contract || !contract.id) {
      toast.error("Impossible d'envoyer le document : contrat invalide");
      return;
    }
    
    if (!folderId) {
      toast.error("Impossible d'envoyer le document : identifiant du dossier manquant");
      return;
    }

    if (!contract.document) {
      toast.error("Aucun document CERFA disponible pour ce contrat");
      return;
    }

    try {
      setSigningInProgress(true);
      
      // Récupérer les données des signataires
      const student = contract.expand?.student_id || {};
      const company = contract.expand?.company_id || {};
      const center = contract.expand?.center_id || {};
      
      // Vérifier les emails
      const missingEmails = [];
      
      if (!company.email && !company.contact_email) missingEmails.push("l'entreprise");
      if (!student.email) missingEmails.push("l'étudiant");
      if (isMinor(student.birth_date) && !student.legal_guardian_email) missingEmails.push("le représentant légal");
      if (!center.email && !center.contact_email) missingEmails.push("le centre de formation");
      
      if (missingEmails.length > 0) {
        toast.error(`Emails manquants pour ${missingEmails.join(', ')}`);
        setSigningInProgress(false);
        return;
      }
      
      // Envoyer le document à la signature avec l'ID du dossier
      const result = await signCerfa(folderId);
      
      // Mettre à jour le contrat localement avec l'ID de procédure
      if (onContractUpdate && result.procedure?.id) {
        onContractUpdate({
          ...contract,
          procedureCerfa: result.procedure.id
        });
      }
      
      // Mettre à jour la liste des signatures
      generateSignaturesList();
      
      toast.success("Document envoyé à la signature avec succès!");
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'envoi du document à la signature");
    } finally {
      setSigningInProgress(false);
    }
  };

  useEffect(() => {
    if (contract) {
      generateSignaturesList();
    }
  }, [contract, signatureStatus]);

  const generateSignaturesList = () => {
    if (!contract || !contract.expand) {
      setLoading(false);
      return;
    }

    // Récupérer les statuts de signature depuis YouSign (nouvelle structure v3)
    const memberStatuses = {};
    if (signatureStatus?.signers) {
      signatureStatus.signers.forEach(signer => {
        if (signer.email) {
          memberStatuses[signer.email.toLowerCase()] = {
            status: signer.status,
            finishedAt: signer.signed_at,
            firstName: signer.first_name,
            lastName: signer.last_name
          };
        }
      });
    }
    console.log('Member statuses (v3):', memberStatuses);

    const student = contract.expand.student_id || {};
    const company = contract.expand.company_id || {};
    const center = contract.expand.center_id || {};
    
    // Vérifier si l'étudiant est mineur
    const studentIsMinor = isMinor(student.birth_date);
    
    // Récupérer les informations complètes des signataires
    const companySignatory = {
      firstName: company.director_first_name || company.contact_first_name || '',
      lastName: company.director_last_name || company.contact_last_name || '',
      fullName: company.director_name || company.contact_name || 
                (company.director_first_name && company.director_last_name ? 
                `${company.director_first_name} ${company.director_last_name}` : 
                (company.contact_first_name && company.contact_last_name ? 
                `${company.contact_first_name} ${company.contact_last_name}` : 
                `Représentant de ${company.name || 'l\'entreprise'}`)),
      email: company.director_email || company.email || '',
      role: company.director_role || company.contact_role || 'Responsable'
    };
    
    const studentSignatory = {
      firstName: student.first_name || '',
      lastName: student.last_name || '',
      fullName: `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Étudiant',
      email: student.email || '',
      role: 'Apprenant'
    };
    
    const centerSignatory = {
      firstName: center.director_first_name || '',
      lastName: center.director_last_name || '',
      fullName: (center.director_first_name && center.director_last_name ? 
                `${center.director_first_name} ${center.director_last_name}` : 
                `Représentant de ${center.name}`),
      email: center.director_email || center.email || '',
      role: 'Directeur du centre'
    };
    
    const parentSignatory = studentIsMinor ? {
      firstName: student.legal_guardian_first_name || '',
      lastName: student.legal_guardian_last_name || '',
      fullName: student.legal_guardian_name || 
                (student.legal_guardian_first_name && student.legal_guardian_last_name ? 
                `${student.legal_guardian_first_name} ${student.legal_guardian_last_name}` : 
                'Parent/Tuteur légal'),
      email: student.legal_guardian_email || '',
      role: 'Représentant légal'
    } : null;
    
    // Créer la liste des signataires pour le CERFA
    const signaturesList = [
      // 1. Société (toujours en premier)
      {
        ordre: 1,
        role: `${companySignatory.role} - Entreprise`,
        signataire: companySignatory.fullName,
        email: companySignatory.email,
        statut: memberStatuses[companySignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
                memberStatuses[companySignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
                memberStatuses[companySignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
                memberStatuses[companySignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[companySignatory.email.toLowerCase()]?.finishedAt || ""
      }
    ];
    
    // 2. Parent légal (si mineur) puis étudiant
    if (studentIsMinor && parentSignatory) {
      // Position 2 : Parent légal
      signaturesList.push({
        ordre: 2,
        role: `${parentSignatory.role}`,
        signataire: parentSignatory.fullName,
        email: parentSignatory.email,
        statut: memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[parentSignatory.email.toLowerCase()]?.finishedAt || ""
      });
      // Position 3 : Étudiant mineur
      signaturesList.push({
        ordre: 3,
        role: `${studentSignatory.role} - Étudiant`,
        signataire: studentSignatory.fullName,
        email: studentSignatory.email,
        statut: memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[studentSignatory.email.toLowerCase()]?.finishedAt || ""
      });
    } else {
      // Position 2 : Étudiant majeur
      signaturesList.push({
        ordre: 2,
        role: `${studentSignatory.role} - Étudiant`,
        signataire: studentSignatory.fullName,
        email: studentSignatory.email,
        statut: memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
                memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[studentSignatory.email.toLowerCase()]?.finishedAt || ""
      });
    }
    
    // Le CFA est en position 3 pour un étudiant majeur ou 4 pour un étudiant mineur
    signaturesList.push({
      ordre: studentIsMinor ? 4 : 3,
      role: `${centerSignatory.role} - CFA`,
      signataire: centerSignatory.fullName,
      email: centerSignatory.email,
      statut: memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
              memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
              memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
              memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
      date: memberStatuses[centerSignatory.email.toLowerCase()]?.finishedAt || ""
    });
    
    setSignatures(signaturesList);
    setLoading(false);
  };

  if (loading) {
    return (
      <Card
        className={cn(styles.card, className)}
        classCardHead={styles.head}
        title="CERFA d'apprentissage"
        classTitle={cn("title-blue", styles.title)}
      >
        <div className={styles.wrapper}>
          <Loader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(styles.card, className)}
      classCardHead={styles.head}
      title="CERFA d'apprentissage"
      classTitle={cn("title-blue", styles.title)}
      head={
        <>
          {contract.document && (
            <Actions
              className={styles.actions}
              items={[
                ...(!contract.procedureCerfa ? [{
                  title: signingInProgress ? "Envoi en cours..." : "Envoyer à la signature",
                  action: handleSignatureRequest,
                  disabled: signingInProgress
                }] : []),
                {
                  title: "Télécharger CERFA",
                  action: handleDownloadCerfa
                }
              ]}
            />
          )}
        </>
      }
    >
      <div className={styles.wrapper}>
        {signatures.length > 0 ? (
          <div className={styles.table}>
            <div className={styles.row}>
              <div className={styles.col}>Ordre</div>
              <div className={styles.col}>Rôle</div>
              <div className={styles.col}>Signataire</div>
              <div className={styles.col}>Statut</div>
              <div className={styles.col}>Date</div>
            </div>
            {signatures.map((item, index) => (
              <div className={styles.row} key={index}>
                <div className={styles.col}>
                  {item.ordre}
                </div>
                <div className={styles.col}>
                  {item.role}
                </div>
                <div className={styles.col}>
                  <div className={styles.product}>{item.signataire}</div>
                  <div className={styles.invoice}>{item.email}</div>
                </div>
                <div className={styles.col}>
                  {item.statut === "Signé" && (
                    <div
                      className={cn(
                        "status-green-dark",
                        styles.status
                      )}
                    >
                      Signé
                    </div>
                  )}
                  {item.statut === "En attente" && (
                    <div
                      className={cn(
                        "status-gray-light",
                        styles.status
                      )}
                    >
                      En attente
                    </div>
                  )}
                  {item.statut === "Refusé" && (
                    <div
                      className={cn(
                        "status-red-dark",
                        styles.status
                      )}
                    >
                      Refusé
                    </div>
                  )}
                </div>
                <div className={styles.col}>
                  {item.date ? new Date(item.date).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : "Non signé"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            Aucune information de signature disponible
          </div>
        )}
      </div>
    </Card>
  );
};

export default Contract;
